/**
 * Language configuration for SyntaxLens
 * Defines supported programming languages with their icons and display properties
 */

export interface Language {
  code: string
  icon: string
  displayName?: string
}

/**
 * Source languages (FROM) - Languages the user knows
 * These are the languages used for providing familiar context
 * Original list: JavaScript, Python, Java, C#, C++, C, Objective-C, TypeScript, R
 */
export const sourceLanguages: Language[] = [
  { code: 'JavaScript', icon: '⚡' },
  { code: 'Python', icon: '🐍' },
  { code: 'Java', icon: '☕' },
  { code: 'PHP', icon: '🐘' },
  { code: 'C#', icon: '🔷', displayName: 'C#' },
  { code: 'C++', icon: '🔧' },
  { code: 'C', icon: '⚙️' },
  { code: 'Objective-C', icon: '📱', displayName: 'Objective-C' },
  { code: 'TypeScript', icon: '📘' },
  { code: 'R', icon: '📊' }
]

/**
 * Target languages (TO) - Languages the user is exploring
 * These are the languages that will receive AI-powered insights
 * New list: Python, TypeScript, Rust, Go, Kotlin, Swift, JavaScript, Java, C#, C++, C, SQL, Julia
 */
export const targetLanguages: Language[] = [
  { code: 'Python', icon: '🐍' },
  { code: 'TypeScript', icon: '📘' },
  { code: 'Rust', icon: '🦀' },
  { code: 'Go', icon: '🐹' },
  { code: 'Kotlin', icon: '🟪' },
  { code: 'Swift', icon: '🍎' },
  { code: 'JavaScript', icon: '⚡' },
  { code: 'Java', icon: '☕' },
  { code: 'C#', icon: '🔷', displayName: 'C#' },
  { code: 'C++', icon: '🔧' },
  { code: 'C', icon: '⚙️' },
  { code: 'SQL', icon: '🗃️' },
  { code: 'Julia', icon: '🔮' }
]

/**
 * Combined list of all supported languages (for backwards compatibility)
 */
export const supportedLanguages: Language[] = [
  ...new Map([...sourceLanguages, ...targetLanguages].map(lang => [lang.code, lang])).values()
]

/**
 * Get language by code
 */
export const getLanguageByCode = (code: string): Language | undefined => {
  return supportedLanguages.find(lang => lang.code === code)
}

/**
 * Get display name for a language (uses displayName if available, otherwise code)
 */
export const getLanguageDisplayName = (code: string): string => {
  const language = getLanguageByCode(code)
  return language?.displayName || language?.code || code
}

/**
 * Get logo path for a language
 * Handles special cases like C#, C++, Objective-C
 */
export const getLogoPath = (languageCode: string): string => {
  // Convert to lowercase but preserve special characters
  const normalized = languageCode.toLowerCase()
  return `/logos/${normalized}.svg`
}

/**
 * Featured language pairs for launch
 * These are the recommended, high-value transition paths
 */
export interface FeaturedPair {
  id: string
  source: string
  target: string
  title: string
  description: string
  gradient: string
  icon: string
  useCase: string
}

export const featuredPairs: FeaturedPair[] = [
  {
    id: 'python-javascript',
    source: 'Python',
    target: 'JavaScript',
    title: 'Python → JavaScript',
    description: 'From Backend to Full-Stack',
    gradient: 'from-blue-500 to-yellow-500',
    icon: '🐍⚡',
    useCase: 'Build web UIs for your Python projects'
  },
  {
    id: 'javascript-typescript',
    source: 'JavaScript',
    target: 'TypeScript',
    title: 'JavaScript → TypeScript',
    description: 'Level Up with Type Safety',
    gradient: 'from-yellow-500 to-blue-600',
    icon: '⚡📘',
    useCase: 'Modern enterprise-grade web development'
  },
  {
    id: 'c-rust',
    source: 'C',
    target: 'Rust',
    title: 'C → Rust',
    description: 'Systems Programming Reimagined',
    gradient: 'from-gray-600 to-orange-600',
    icon: '⚙️🦀',
    useCase: 'Memory-safe systems programming'
  }
]

/**
 * Default source and target languages for initial state
 */
export const defaultLanguages = {
  source: 'Python',
  target: 'JavaScript'
}